import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { canonicalPath } from "../../boundaries/url-path.parser";
import { JOURNEY_ROLE } from "../../enums/scan";
import { clusterLabel } from "../scan/estateFormat";
import { issueLinkMatchesPage } from "../scan/issuePageResolution";
import { classifyJourneyRole, journeyRoleLabel } from "../scan/journeyRoles";
import {
  clusterContextForPages,
  clusterKey,
  contentPathSegments,
} from "../scan/scanModel";
import { isOpen } from "../website/issueModel";
import type { MapCurrentGroup, MapPage, MapPageFact } from "./mapTypes";

export function buildMapPageFacts(data: OverviewResponse): MapPageFact[] {
  const clusterContext = clusterContextForPages(data.pages);
  const pagesByGroup = new Map<string, OverviewResponse["pages"]>();
  for (const page of data.pages) {
    const key = clusterKey(page.path, clusterContext);
    pagesByGroup.set(key, [...(pagesByGroup.get(key) ?? []), page]);
  }
  const validIds = new Set(data.pages.map((page) => page.id));
  const incoming = new Map<string, number>();
  const outgoing = new Map<string, number>();
  const seen = new Set<string>();
  for (const link of data.links) {
    const key = `${link.fromPageId}:${link.toPageId}`;
    if (
      seen.has(key) ||
      link.fromPageId === link.toPageId ||
      !validIds.has(link.fromPageId) ||
      !validIds.has(link.toPageId)
    ) {
      continue;
    }
    seen.add(key);
    outgoing.set(link.fromPageId, (outgoing.get(link.fromPageId) ?? 0) + 1);
    incoming.set(link.toPageId, (incoming.get(link.toPageId) ?? 0) + 1);
  }
  const openIssues = data.issues.filter(isOpen);
  const facts = data.pages.map((page) => {
    const inboundCount = incoming.get(page.id) ?? 0;
    const outboundCount = outgoing.get(page.id) ?? 0;
    const role = classifyJourneyRole({
      page,
      inboundCount,
      outboundCount,
    });
    const groupKey = clusterKey(page.path, clusterContext);
    return {
      page,
      groupKey,
      groupLabel: clusterLabel(groupKey, pagesByGroup.get(groupKey)),
      role,
      roleLabel: journeyRoleLabel(role),
      inboundCount,
      outboundCount,
      issues: openIssues.filter((issue) =>
        issue.pages.some((link) => issueLinkMatchesPage(link, page)),
      ),
    };
  });
  return deduplicateFactsByPath(facts);
}

export function groupMapFacts(facts: MapPageFact[]): MapCurrentGroup[] {
  const groups = new Map<string, MapPageFact[]>();
  for (const fact of facts) {
    groups.set(fact.groupKey, [...(groups.get(fact.groupKey) ?? []), fact]);
  }
  return [...groups.entries()]
    .map(([key, rows]) => ({
      key,
      label: rows[0]?.groupLabel ?? clusterLabel(key),
      facts: rows,
    }))
    .sort(
      (left, right) =>
        right.facts.length - left.facts.length ||
        left.label.localeCompare(right.label),
    );
}

export function relatedPageIds(
  data: OverviewResponse,
  pages: MapPage[],
): string[] {
  const sourceIds = new Set(pages.map((page) => page.id));
  return [
    ...new Set(
      data.links
        .filter(
          (link) =>
            sourceIds.has(link.fromPageId) && !sourceIds.has(link.toPageId),
        )
        .map((link) => link.toPageId),
    ),
  ];
}

export function relatedGroupLabels(
  data: OverviewResponse,
  facts: MapPageFact[],
  allFacts: MapPageFact[],
): string[] {
  const byId = new Map(allFacts.map((fact) => [fact.page.id, fact]));
  const sourceIds = new Set(facts.map((fact) => fact.page.id));
  const counts = new Map<string, number>();
  for (const link of data.links) {
    if (!sourceIds.has(link.fromPageId)) continue;
    const target = byId.get(link.toPageId);
    if (!target || sourceIds.has(target.page.id)) continue;
    counts.set(target.groupLabel, (counts.get(target.groupLabel) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort(
      ([leftLabel, leftCount], [rightLabel, rightCount]) =>
        rightCount - leftCount || leftLabel.localeCompare(rightLabel),
    )
    .map(([label]) => label);
}

export function hasSectionHub(
  group: MapCurrentGroup,
  clusterContext: ReturnType<typeof clusterContextForPages>,
): boolean {
  return group.facts.some((fact) => {
    const segments = contentPathSegments(fact.page.path, clusterContext);
    return segments.length === 1 && segments[0] === group.key;
  });
}

export function groupNeedsClientContext(group: MapCurrentGroup): boolean {
  if (group.facts.length < 2) return false;
  const unclassified = group.facts.some(
    (fact) => fact.role === JOURNEY_ROLE.UNCLASSIFIED,
  );
  const connected = group.facts.some(
    (fact) => fact.inboundCount + fact.outboundCount > 0,
  );
  const assessed = group.facts.filter(
    (fact) => fact.page.contentFitness !== null,
  ).length;
  return unclassified || (!connected && assessed < group.facts.length);
}

function deduplicateFactsByPath(facts: MapPageFact[]): MapPageFact[] {
  const byPath = new Map<string, MapPageFact>();
  for (const fact of facts) {
    const key = canonicalPath(fact.page.path);
    const existing = byPath.get(key);
    if (!existing) {
      byPath.set(key, fact);
      continue;
    }
    const preferred =
      existing.page.contentFitness === null && fact.page.contentFitness !== null
        ? fact
        : existing;
    byPath.set(key, {
      ...preferred,
      inboundCount: Math.max(existing.inboundCount, fact.inboundCount),
      outboundCount: Math.max(existing.outboundCount, fact.outboundCount),
      issues: [
        ...new Map(
          [...existing.issues, ...fact.issues].map((issue) => [
            issue.id,
            issue,
          ]),
        ).values(),
      ],
    });
  }
  return [...byPath.values()];
}
